/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useForm } from "react-hook-form";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";

import * as MLSAActions from "Dataflows/UserProfile/UserProfileAction";
import * as MLSABenefitsSelector from "Dataflows/UserProfile/UserProfileSelector";

import { useAuth } from "Authentication/AuthProvider";
import VisualStudioBenefitDetailSidePanel from "Components/ProfileVisualStudioBenefitDetails/VisualStudioBenefitDetailSidePanel";
import VisualStudioBenefitDetail from "Components/VisualStudioBenefitDetail/VisualStudioBenefitDetail";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import {
  benefitNameToResultMap,
  BenefitNameEnum,
  tenantBenefitTypes,
  BenefitDisplayNameEnum,
} from "Constants/BenefitConstants";
import { OperationStatus } from "Constants/OperationStatus";
import {
  activeProgramProfileStatus,
  newMilestone,
  studentPartnerEmailDomain,
  SwagKitType,
} from "Constants/ProfileConstants";
import { RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { AppState } from "Dataflows/Reducers";
import { IUserProfile } from "Interfaces/UserProfile/IUserProfile";
import { getTenant } from "Utility/Utils";
import KeyBenefitsList from "Components/KeyBenefitsList/KeyBenefitsList";
import { SidePanel } from "Components/SidePanel/SidePanel";
import {
  benefitLabelStyles,
  containerStylesWithMarginTop,
  horizontalGapStackTokens,
} from "Components/ProfileBenefitDetails/ProfileBenefitDetails.styles";
import { IProfileBenefitDetailsProps } from "Components/ProfileBenefitDetails/IProfileBenefitDetailsProps";

const StudentBenefitDetails = (props: IProfileBenefitDetailsProps): React.ReactElement => {
  const {
    claimingBenefitError,
    isClaimingBenefitStatus,
    claimBenefitReset,
    claimBenefit,
    getProfileBenefits,
    tenantName,
  } = props;
  const profileBenefits = useSelector(MLSABenefitsSelector.getProfileBenefits);
  const profileBenefitsErrorMessage = useSelector(
    MLSABenefitsSelector.getProfileBenefitsErrorMessage
  );

  const { id } = useParams<{ id: string }>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const [benefitToClaim, setBenefitToClaim] = useState("");
  const isClaimingBenefit = isClaimingBenefitStatus === OperationStatus.Pending;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        SwagKitType: Yup.string().required("Benefit Type is required"),
      })
    ),
  });

  const { hasRole } = useAuth();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(MLSAActions.getProfileBenefits(parseInt(id)));
  }, [id]);

  const profileBenefitsStatus = useSelector(MLSABenefitsSelector.getProfileBenefitsStatus);

  const selectedUserProfile = useSelector<AppState, IUserProfile>((state) =>
    MLSABenefitsSelector.getUserProfile(state.userProfileState)
  );
  const visualStudioBenefitOperationStatus = useSelector(
    MLSABenefitsSelector.getUpdateVSProfileBenefitStatus
  );
  const visualStudioBenefitError = useSelector(
    MLSABenefitsSelector.getUpdateVSProfileBenefitsError
  );

  const canManageBenefits =
    hasRole(RolesEnum.MSA_BENEFITS_MANAGER) || hasRole(RolesEnum.MSA_PROGRAM_OWNER);

  const hasStudentPartnerEmail =
    selectedUserProfile?.studentPartnerEmail?.toLowerCase().includes(studentPartnerEmailDomain) ||
    selectedUserProfile?.primaryEmail?.toLowerCase().includes(studentPartnerEmailDomain) ||
    selectedUserProfile?.secondaryEmail?.toLowerCase().includes(studentPartnerEmailDomain);
  const canEnableBenefits =
    selectedUserProfile?.levelStatus?.levelName !== newMilestone &&
    selectedUserProfile?.programProfileStatusName === activeProgramProfileStatus &&
    selectedUserProfile?.tenants.includes(TenantTypesEnum.MSP) &&
    hasStudentPartnerEmail;

  const assignBenefitClickedVs = (benefitType: BenefitNameEnum): void => {
    switch (benefitType) {
      case BenefitNameEnum.VisualStudio:
        setIsPanelOpen(true);
        break;
      default:
        break;
    }
  };

  const getBenefitOtherTenantName = (benefitType: BenefitNameEnum): string | undefined => {
    if (!profileBenefits.benefitTenant) return undefined;

    const benefitTenant = getTenant(profileBenefits.benefitTenant[benefitType]);

    if (benefitTenant?.toLowerCase() === TenantTypesEnum.MLSA.toLowerCase()) {
      return undefined;
    }

    return benefitTenant;
  };

  const getAvailableBenefitTypes = (): BenefitNameEnum[] => {
    return tenantBenefitTypes[TenantTypesEnum.MSP];
  };

  const getProfileBenefitsMap = (benefitType: BenefitNameEnum) => {
    return profileBenefits[benefitNameToResultMap[benefitType]];
  };

  const assignBenefitClicked = (benefitType: string): void => {
    setIsAssignPanelOpen(true);
    setBenefitToClaim(benefitType);
  };

  const dismissAssignPanel = (): void => {
    setIsAssignPanelOpen(false);
    dispatch(getProfileBenefits(parseInt(id)));
    dispatch(claimBenefitReset());
    reset(); // to reset benefit-type drop-down
  };

  const claimBenefitDispatch = () =>
    handleSubmit((formData) => {
      dispatch(claimBenefit(benefitToClaim, parseInt(id), formData.SwagKitType));
    });

  useEffect(() => {
    if (isClaimingBenefitStatus === OperationStatus.Succeeded) {
      dismissAssignPanel();
    }
  }, [isClaimingBenefit, isClaimingBenefitStatus, claimingBenefitError]);

  const benefitLists = getAvailableBenefitTypes().map((benefitType, index) => {
    if (benefitType === BenefitNameEnum.VisualStudio) {
      return (
        <>
          <VisualStudioBenefitDetail
            key={index}
            benefitType={BenefitNameEnum.VisualStudio}
            benefits={getProfileBenefitsMap(benefitType) ?? []}
            assignBenefitClicked={assignBenefitClickedVs}
            canManageBenefits={canManageBenefits && canEnableBenefits}
            benefitOtherTenantName={getBenefitOtherTenantName(BenefitNameEnum.VisualStudio)}
          />
          <VisualStudioBenefitDetailSidePanel
            isPanelOpen={isPanelOpen}
            setIsPanelOpen={setIsPanelOpen}
            canEnableBenefits={canEnableBenefits}
            isProvisioned={getProfileBenefitsMap(benefitType)?.[0]?.isProvisioned}
            currentStatus={
              getProfileBenefitsMap(benefitType)?.[0]?.isProvisioned
                ? "Assigned"
                : getProfileBenefitsMap(benefitType)?.[0]?.subscriptionGuid
                ? "Removed"
                : "Not Assigned"
            }
            operationStatus={visualStudioBenefitOperationStatus}
            errorMessage={visualStudioBenefitError}
            assignBenefitClicked={(): MLSAActions.UserProfileActionTypes =>
              dispatch(MLSAActions.provisionUserProfileVSBenefit(selectedUserProfile.id))
            }
            removeBenefitClicked={(): MLSAActions.UserProfileActionTypes =>
              dispatch(MLSAActions.endUserProfilVSBenefit(selectedUserProfile.id))
            }
            extendBenefitClicked={(): MLSAActions.UserProfileActionTypes =>
              dispatch(MLSAActions.extendUserProfileVSBenefit(selectedUserProfile.id))
            }
            getProfileBenefits={(): MLSAActions.UserProfileActionTypes =>
              dispatch(MLSAActions.getProfileBenefits(selectedUserProfile.id))
            }
            claimBenefitReset={(): MLSAActions.UserProfileActionTypes =>
              dispatch(MLSAActions.userProfileVSBenefitReset())
            }
          />
        </>
      );
    } else {
      return (
        <KeyBenefitsList
          key={index}
          benefitType={benefitType}
          benefits={getProfileBenefitsMap(benefitType) ?? []}
          assignBenefitClicked={assignBenefitClicked}
          canManageBenefits={canManageBenefits}
          userProfileId={parseInt(id)}
          benefitOtherTenantName={getBenefitOtherTenantName(benefitType)}
          tenantName={tenantName}
        />
      );
    }
  });

  return (
    <>
      {profileBenefitsStatus === OperationStatus.Failed ? (
        <Text>{profileBenefitsErrorMessage}</Text>
      ) : profileBenefitsStatus === OperationStatus.Pending ? (
        <Spinner size={SpinnerSize.large} />
      ) : (
        <form>
          <SidePanel
            isOpen={isAssignPanelOpen}
            title="Add Benefits"
            saveButtonLabel="Claim"
            error={claimingBenefitError}
            disableActions={isClaimingBenefit}
            onDismiss={dismissAssignPanel}
            onSave={claimBenefitDispatch()}
          >
            <Stack>
              <Text>You can claim a benefit and assign to active users</Text>
            </Stack>
            <Stack horizontal tokens={horizontalGapStackTokens}>
              <StackItem>
                <Text styles={benefitLabelStyles}>Benefit:</Text>
              </StackItem>
              <StackItem>
                <Text>
                  {benefitToClaim === BenefitNameEnum.SwagKit
                    ? BenefitDisplayNameEnum.SwagKit
                    : benefitToClaim}
                </Text>
              </StackItem>
            </Stack>
            <Stack styles={containerStylesWithMarginTop}>
              <ComboBoxController
                isRequired={true}
                control={control}
                errors={errors}
                inputName="SwagKitType"
                labelDisplayName="Benefit Type"
                options={SwagKitType}
                placeholderText="Select Benefit Type"
              />
            </Stack>
            <Stack>
              <Text>
                {`Assign a ${
                  benefitToClaim === BenefitNameEnum.SwagKit
                    ? BenefitDisplayNameEnum.SwagKit
                    : benefitToClaim
                } code to a Community Leader. This code will be available
                for the user under Account - Benefits - ${
                  benefitToClaim === BenefitNameEnum.SwagKit
                    ? BenefitDisplayNameEnum.SwagKit
                    : benefitToClaim
                }. You should notify
                the user that a new code has been assigned.`}
              </Text>
            </Stack>
          </SidePanel>
          <>{benefitLists}</>
        </form>
      )}
    </>
  );
};

export default StudentBenefitDetails;
